import { useEffect, useId, useRef, useState, type CSSProperties } from 'react';
import { createPortal } from 'react-dom';
import { useTranslation } from 'react-i18next';
import { LITE_SIGNUP_URL, SIGNUP_URL } from '../data/links';

type StartChooserProps = {
  /** Text on the trigger button. */
  label: string;
  /** Classes for the trigger button, so each page can keep its own button style. */
  className: string;
  style?: CSSProperties;
};

type StartOption = { href: string; icon: string; title: string; text: string };

/**
 * "Start for free" asks one question in a centred dialog, then sends the visitor to the right
 * sign-up: one hub and a small fleet goes to Lite, anything larger goes to Command. The dialog is
 * portalled to <body> so the hero's overflow and animations can never clip or overlap it.
 */
export default function StartChooser({ label, className, style }: StartChooserProps) {
  const { t } = useTranslation();
  const [open, setOpen] = useState(false);
  const triggerRef = useRef<HTMLButtonElement>(null);
  const firstOptionRef = useRef<HTMLAnchorElement>(null);
  const titleId = useId();

  useEffect(() => {
    if (!open) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setOpen(false);
    };
    const prevOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    document.addEventListener('keydown', onKey);
    firstOptionRef.current?.focus();
    const trigger = triggerRef.current;
    return () => {
      document.body.style.overflow = prevOverflow;
      document.removeEventListener('keydown', onKey);
      trigger?.focus();
    };
  }, [open]);

  const options: StartOption[] = [
    { href: LITE_SIGNUP_URL, icon: 'ri-store-2-line', title: t('home.hero.chooser.liteTitle'), text: t('home.hero.chooser.liteText') },
    { href: SIGNUP_URL, icon: 'ri-building-4-line', title: t('home.hero.chooser.commandTitle'), text: t('home.hero.chooser.commandText') },
  ];

  return (
    <>
      <button
        ref={triggerRef}
        type="button"
        onClick={() => setOpen(true)}
        aria-haspopup="dialog"
        aria-expanded={open}
        className={className}
        style={style}
      >
        {label}
      </button>

      {open && typeof document !== 'undefined'
        ? createPortal(
            <div className="fixed inset-0 z-[2147483600] flex items-end justify-center p-4 sm:items-center" role="presentation">
              <button
                type="button"
                aria-label={t('home.hero.chooser.close')}
                onClick={() => setOpen(false)}
                className="absolute inset-0 cursor-default bg-slate-950/40 backdrop-blur-[2px]"
              />
              <div
                role="dialog"
                aria-modal="true"
                aria-labelledby={titleId}
                className="relative w-full max-w-md rounded-2xl bg-white p-6 shadow-[0_30px_80px_-20px_rgba(15,40,90,0.45)]"
              >
                <button
                  type="button"
                  onClick={() => setOpen(false)}
                  aria-label={t('home.hero.chooser.close')}
                  className="absolute end-4 top-4 flex h-8 w-8 items-center justify-center rounded-lg text-slate-400 transition-colors hover:bg-slate-100 hover:text-slate-700"
                >
                  <i className="ri-close-line text-xl" aria-hidden />
                </button>
                <h2 id={titleId} className="pe-8 text-lg font-bold tracking-tight text-slate-950">{t('home.hero.chooser.title')}</h2>
                <p className="mt-1 text-sm text-slate-500">{t('home.hero.chooser.subtitle')}</p>
                <div className="mt-5 space-y-3">
                  {options.map((o, i) => (
                    <a
                      key={o.href}
                      ref={i === 0 ? firstOptionRef : undefined}
                      href={o.href}
                      target="_blank"
                      rel="noopener noreferrer"
                      onClick={() => setOpen(false)}
                      className="group flex items-start gap-3 rounded-xl border border-slate-200 p-4 transition-colors hover:border-blue-300 hover:bg-blue-50/60 focus-visible:border-blue-400 focus-visible:outline-none"
                    >
                      <span className="flex h-10 w-10 flex-none items-center justify-center rounded-lg bg-blue-50 text-[#1769ff] group-hover:bg-white">
                        <i className={`${o.icon} text-lg`} aria-hidden />
                      </span>
                      <span className="min-w-0 flex-1">
                        <span className="block text-[15px] font-semibold text-slate-900">{o.title}</span>
                        <span className="mt-0.5 block text-[13px] leading-snug text-slate-500">{o.text}</span>
                      </span>
                      <i className="ri-arrow-right-line mt-2.5 text-slate-400 transition-transform group-hover:translate-x-0.5 group-hover:text-[#1769ff] rtl:rotate-180" aria-hidden />
                    </a>
                  ))}
                </div>
              </div>
            </div>,
            document.body
          )
        : null}
    </>
  );
}
